'use client'

import { useState, type LegacyRef } from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import { Icon, Image, type ImageData } from '@media-components'
import {
  SectionWrapper,
  WrapperHeader,
  ActionButton,
  type ActionButtonData,
  type SectionWrapperProps,
} from '@components/common'
import type { StyleClassNames } from '@components/common/WrapperHeader/styles'
import { useForm } from '@utils/form'
import { useReCaptcha } from '@utils/hooks'
import { EmailPattern, publicImages } from '@utils/shared'
import { useSnackBar } from '@utils/snackbar'
import { BaselineStyleClasses, CustomStyles, getStyles } from '@utils/styles'
import { useLeadGeneration } from 'src/client-query/lead'
import defaultStyles, { StylesClassNames } from './styles'

export type ExploreBannerData = {
  featureName?: string
  heading?: string
  description?: string
  bulletPointsData?: string[]
  imageUrl?: ImageData
  iconUrl?: ImageData
  actionButtonData?: ActionButtonData
}

export type ExploreBannerProps = ExploreBannerData & {
  csx?: CustomStyles<StylesClassNames>
  wrapperHeaderCsx?: CustomStyles<StyleClassNames>
  placeholderText?: string
  buttonText?: string
  showTextField?: boolean
  sectionWrapperProps?: Partial<SectionWrapperProps>
}
export function ConsultationSection({
  bulletPointsData,
  featureName,
  heading,
  description,
  imageUrl = { src: publicImages.newDashboard, alt: 'newDashboard' },
  buttonText,
  placeholderText,
  showTextField = true,
  csx,
  iconUrl,
  sectionWrapperProps,
  wrapperHeaderCsx,
  actionButtonData,
}: ExploreBannerProps) {
  const styles = getStyles(defaultStyles, csx)
  const { show } = useSnackBar()
  const tFeature = useTranslations('NewsLetterSection')
  const t = useTranslations('ConsultationSection')
  const resolvedButtonText = buttonText ?? tFeature('buttonText')
  const [submitting, setSubmitting] = useState(false)
  const { handleSubmit, register, reset } = useForm({
    initialValues: {
      email: '',
    },
    validationRules: {
      email: {
        required: true,
        message: t('validation.email.message'),
        pattern: {
          value: EmailPattern,
          message: t('validation.pattern.message'),
        },
      },
    },
  })

  const { createSoftLead } = useLeadGeneration({
    onFailure: () => {
      show.error({
        title: t('error.message'),
      })
    },
    onSuccess: () => {
      show.success({
        title: t('success.message'),
      })
    },
  })

  const { verifyCaptcha } = useReCaptcha()

  const { ref, ...restSectionWrapperProps } = sectionWrapperProps || {}

  const onSubmit = async (data: { email: string }) => {
    setSubmitting(true)
    try {
      const freshToken = await verifyCaptcha('newsletter_submit')
      await createSoftLead({
        formData: {
          email: data.email,
        },
        pdpCategory: 'NEWSLETTER',
        pdpCategoryId: 'NEWSLETTER',
        captchaToken: freshToken || undefined,
        deepLink: '',
        pdpId: '',
        pdpTitle: '',
        message: '',
        tags: ['NEWSLETTER'],
      })

      reset()
    } catch (error) {
      await verifyCaptcha('newsletter_retry')
      show.error({
        title: t('error.message'),
      })
    } finally {
      setSubmitting(false)
      await verifyCaptcha('newsletter_idle')
    }
  }
  return (
    <SectionWrapper
      ref={ref as LegacyRef<HTMLDivElement>}
      {...restSectionWrapperProps}
    >
      <Box
        {...styles('wrapper')}
        className={BaselineStyleClasses.imageTransitioningWrapper}
      >
        <Stack
          spacing={2}
          {...styles('wrapperHeader')}
          component="form"
          onSubmit={(e) => {
            e.preventDefault()
            handleSubmit((data) => void onSubmit(data))(e)
          }}
        >
          <WrapperHeader
            featureName={featureName}
            text={heading || tFeature('heading')}
            darkBg
            image={iconUrl}
            csx={wrapperHeaderCsx}
          />

          {description && (
            <Typography
              variant="body1.400"
              color="text.primaryDark"
              sx={{ maxWidth: '742px' }}
            >
              {description}
            </Typography>
          )}

          {!!bulletPointsData?.length && (
            <Stack gap="16px">
              {bulletPointsData.map((item, i) => (
                <Box key={i} {...styles('listItem')}>
                  <Icon iconName="TickCircle" colorValues="green.500" />
                  <Typography variant="body2.500" color="text.primaryDark">
                    {item}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}

          <Box {...styles('formWrapper')}>
            {actionButtonData && !showTextField ? (
              <ActionButton
                {...actionButtonData}
                size="large"
                showArrow
                sx={{
                  ...actionButtonData.sx,
                }}
              />
            ) : (
              <Button
                endIcon={
                  <Icon iconName="ArrowRight" colorValues="neutral.white" />
                }
                size="large"
                {...styles('button', {
                  '&.Mui-disabled': {
                    backgroundColor: 'blue.400',
                    color: 'neutral.white',
                    cursor: 'not-allowed !important',
                  },
                })}
                type="submit"
                disabled={submitting}
              >
                {submitting ? 'Subscribing...' : resolvedButtonText}
              </Button>
            )}
          </Box>
        </Stack>

        <Image
          src={imageUrl.src}
          alt={imageUrl.alt}
          {...styles('image')}
          className={BaselineStyleClasses.imageTransition}
        />
      </Box>
    </SectionWrapper>
  )
}
